/* eslint-disable max-lines */
import React, {RefObject, forwardRef} from 'react';

import {
    AddressField,
    PropsType as AddressFieldProps,
} from 'desktop/components/addressField/addressField';
import {Input, PropsType as InputProps} from 'desktop/components/input/input';
import {Select, PropsType as SelectProps} from 'desktop/components/select/select';
import {PropsType as AddressFormEmailPropsType} from 'desktop/pages/checkout/components/addressFormEmail/addressFormEmail';

import {CountriesType, StatesType} from 'types/locationType';

type CountryComponentType = Omit<SelectProps, 'options'> & {
    error: boolean;
    success: boolean;
    countries: CountriesType[];
    attributes: {[key: string]: string | number};
};

export type InputComponentType = Omit<InputProps, 'onChange'> & {
    error: boolean;
    success: boolean;
    attributes: {[key: string]: string | number};
    fieldRef?: RefObject<HTMLInputElement>;
    onChange: (value: string) => void;
};

export type StateComponentType = Omit<SelectProps, 'options'> & {
    error: boolean;
    success: boolean;
    states: StatesType;
    country: string;
    countries: CountriesType[];
    attributes: {[key: string]: string | number};
};

type AddressFieldType = AddressFieldProps & {
    error: boolean;
    success: boolean;
    states: StatesType;
    country: string;
    countries: CountriesType[];
    attributes: {[key: string]: string | number};
};

export type ComponentType =
    | React.FC<StateComponentType>
    | React.FC<CountryComponentType>
    | React.FC<AddressFieldType>
    | React.ExoticComponent<InputComponentType>
    | React.FC<AddressFormEmailPropsType>;

/**
 * Take only necessary props and returns Input component
 * @param {InputComponentType} props - props for component
 * @returns {FC} - Returned Input component
 */
export const InputComponent = forwardRef(
    (
        {attributes, errorMessage, label, onBlur, onChange, success, value}: InputComponentType,
        ref: React.Ref<HTMLInputElement>,
    ) => (
        <Input
            {...attributes}
            errorMessage={errorMessage}
            label={label}
            onBlur={onBlur}
            onChange={e => onChange(e.target.value)}
            ref={ref}
            success={success}
            value={value}
        />
    ),
);

InputComponent.displayName = 'InputComponent';

/**
 * Get country
 * @param {CountryComponentType} props - props for component
 * @return {FC} country select component
 */
export const getCountryComponent: React.FC<CountryComponentType> = ({
    attributes: {name},
    countries,
    error,
    label,
    onChange,
    success,
    value,
}) => (
    <Select
        options={countries.map(country => ({
            label: country.label,
            value: country.value,
        }))}
        error={error}
        label={label}
        name={String(name)}
        onChange={onChange}
        success={success}
        value={value}
    />
);

/**
 * Take only necessary props and returns Select component
 * @param {StateComponentType} props - props for component
 * @returns {FC} - Returned Input component
 */
export const getStateComponent: React.FC<StateComponentType> = props => {
    const {
        attributes: {name, statesNumber, ...attrs},
        country,
        error,
        label,
        onChange,
        states,
        success,
        value,
    } = props;

    if (!statesNumber) {
        return (
            <InputComponent
                {...props}
                attributes={{...attrs, name}}
                value={value === '0' || !value ? '' : String(value)}
            />
        );
    }

    return (
        <Select
            options={states[country].map(state => ({
                label: state.label,
                value: state.label,
            }))}
            error={error}
            label={`* ${label}`}
            name={String(name)}
            onChange={onChange}
            success={success}
            value={value || '0'}
        />
    );
};

/**
 * Take only necessary props and returns Address component
 * @param {AddressFieldType} props - props for component
 * @returns {FC} - Returned Input component
 */
export const getAddressComponent: React.FC<AddressFieldType> = ({
    attributes,
    country,
    errorMessage,
    label,
    onAutocomplete,
    onBlur,
    onChange,
    success,
    value,
}) => (
    <AddressField
        attributes={attributes}
        country={country}
        errorMessage={errorMessage}
        label={label}
        onAutocomplete={onAutocomplete}
        onBlur={onBlur}
        onChange={onChange}
        success={success}
        value={value}
    />
);

/**
 * Take only necessary props and returns Input Optional component
 * @param {InputComponentType} props - props for component
 * @returns {FC} - Returned Input component
 */
export const getInputOptionalComponent: React.FC<InputComponentType> = ({
    attributes,
    error,
    fieldRef,
    label,
    onBlur,
    onChange,
    success,
    value,
}) => (
    <Input
        {...attributes}
        errorMessage={error}
        label={label}
        onBlur={onBlur}
        onChange={e => onChange(e.target.value)}
        ref={fieldRef}
        success={success}
        value={value}
    />
);

/**
 * Format phone number
 * @param {string} value - Phone number
 * @return {string} - formatted phone number
 */
export const formatPhoneNumber = (value: string): string => {
    // only allows 0-9 inputs
    const currentValue = value.replace(/[^\d]/g, '');
    const cvLength = currentValue.length;

    // returns: "x", "xx", "xxx"
    if (cvLength < 4) return currentValue;

    // returns: "(xxx)", "(xxx) x", "(xxx) xx", "(xxx) xxx",
    if (cvLength < 7) return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3)}`;

    // returns: "(xxx) xxx-", (xxx) xxx-x", "(xxx) xxx-xx", "(xxx) xxx-xxx", "(xxx) xxx-xxxx"
    if (cvLength < 11)
        return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3, 6)}-${currentValue.slice(
            6,
            10,
        )}`;

    return currentValue;
};
